import React, { useEffect, useState } from 'react';
import { fetchMalScore, getCachedMalScore, MalScore } from '../services/malScore';

export function useMalScore(title?: string, year = 0): MalScore | null {
  const [score, setScore] = useState<MalScore | null>(
    () => getCachedMalScore(title, year) ?? null
  );

  useEffect(() => {
    let alive = true;
    setScore(getCachedMalScore(title, year) ?? null);
    fetchMalScore(title, year).then((s) => {
      if (alive) setScore(s);
    });
    return () => {
      alive = false;
    };
  }, [title, year]);

  return score;
}

interface MalScoreBadgeProps {
  title?: string;
  year?: number;
  /** Shown when MAL has no score for this title (e.g. a manually entered score) */
  fallback?: string;
  className?: string;
  /** Hide the "MAL" tag on phones where space is tight */
  compact?: boolean;
}

/** ⭐ MAL "Scored" pill; renders nothing until there's a score to show */
export const MalScoreBadge: React.FC<MalScoreBadgeProps> = ({
  title,
  year = 0,
  fallback,
  className = '',
  compact = false,
}) => {
  const mal = useMalScore(title, year);
  const text = mal ? mal.score.toFixed(2) : fallback;
  if (!text) return null;
  return (
    <span
      title={mal ? `คะแนน MyAnimeList: ${text}` : `คะแนน: ${text}`}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-black/80 text-amber-300 font-bold border border-amber-400/40 whitespace-nowrap leading-none ${className}`}
    >
      <span>⭐</span>
      <span>{text}</span>
      {mal && (
        <span className={`text-[0.8em] text-sky-300 font-extrabold ${compact ? 'hidden sm:inline' : ''}`}>
          MAL
        </span>
      )}
    </span>
  );
};
